import type { Edge, Node } from "@xyflow/react";

interface HubLayoutOptions {
  systemId: string;
  actorTopGap?: number;
  externalTopGap?: number;
  externalRowGap?: number;
  externalColGap?: number;
  externalsPerRow?: number;
  /**
   * L1 arrangement (gap #6/10):
   *   "sides"   — actors above the system, externals stacked below in rows
   *               of `externalsPerRow`. The current/default layout.
   *   "compass" — actors stacked in a left column, externals stacked in a
   *               right column. Externals arrive cluster-sorted from
   *               buildL1, so a sequential vertical stack preserves
   *               cluster groups automatically and the cluster-frame
   *               bbox remains a tidy vertical rectangle.
   */
  mode?: "sides" | "compass";
}

// Bumped from 80 to 120 alongside gap #4's SystemNode slim. The declared
// SYSTEM_NODE_SIZE shrank to 140px tall, but the rendered <div> can still
// overflow a few px in either direction; this guarantees a comfortable
// clear of 40px+ between actors and the system box at any reasonable zoom.
const DEFAULT_ACTOR_TOP_GAP = 120;
const DEFAULT_EXT_TOP_GAP = 100;
const DEFAULT_EXT_ROW_GAP = 50;
const DEFAULT_EXT_COL_GAP = 60;
const DEFAULT_EXT_PER_ROW = 4;
// Compass arrangement (gap #6/10).
const COMPASS_SIDE_GAP = 100; // horizontal gap between system edge and each column
const COMPASS_STACK_GAP = 32; // vertical gap between cards within a column

function nodeSize(node: Node, fallbackW = 200, fallbackH = 80) {
  const w =
    typeof node.width === "number" ? node.width : ((node.style?.width as number) ?? fallbackW);
  const h =
    typeof node.height === "number" ? node.height : ((node.style?.height as number) ?? fallbackH);
  return { w, h };
}

function rowWidth(nodesInRow: Node[], gap: number): number {
  if (nodesInRow.length === 0) return 0;
  let total = 0;
  for (let i = 0; i < nodesInRow.length; i += 1) {
    total += nodeSize(nodesInRow[i] as Node).w;
    if (i > 0) total += gap;
  }
  return total;
}

function rowHeight(nodesInRow: Node[]): number {
  let max = 0;
  for (const n of nodesInRow) {
    const { h } = nodeSize(n);
    if (h > max) max = h;
  }
  return max;
}

function placeRow(
  nodesInRow: Node[],
  centerY: number,
  gap: number,
  positioned: Map<string, { x: number; y: number }>,
) {
  const width = rowWidth(nodesInRow, gap);
  let cursor = -width / 2;
  for (const node of nodesInRow) {
    const { w, h } = nodeSize(node);
    positioned.set(node.id, { x: cursor, y: centerY - h / 2 });
    cursor += w + gap;
  }
}

function placeColumn(
  nodesInColumn: Node[],
  centerX: number,
  gap: number,
  positioned: Map<string, { x: number; y: number }>,
) {
  // Stack nodes vertically, centered around y=0 (the system's vertical
  // midline). Each node's x is its column-center minus half its own width
  // — supports mixed-width children even though L1 declared widths are
  // currently uniform.
  let totalH = 0;
  for (let i = 0; i < nodesInColumn.length; i += 1) {
    totalH += nodeSize(nodesInColumn[i] as Node).h;
    if (i > 0) totalH += gap;
  }
  let cursorY = -totalH / 2;
  for (const node of nodesInColumn) {
    const { w, h } = nodeSize(node);
    positioned.set(node.id, { x: centerX - w / 2, y: cursorY });
    cursorY += h + gap;
  }
}

export function radialLayout(
  nodes: Node[],
  edges: Edge[],
  options: HubLayoutOptions,
): { nodes: Node[]; edges: Edge[] } {
  const { systemId } = options;
  const actorTopGap = options.actorTopGap ?? DEFAULT_ACTOR_TOP_GAP;
  const extTopGap = options.externalTopGap ?? DEFAULT_EXT_TOP_GAP;
  const extRowGap = options.externalRowGap ?? DEFAULT_EXT_ROW_GAP;
  const extColGap = options.externalColGap ?? DEFAULT_EXT_COL_GAP;
  const perRow = options.externalsPerRow ?? DEFAULT_EXT_PER_ROW;
  const mode = options.mode ?? "sides";

  const positioned = new Map<string, { x: number; y: number }>();

  const systemNode = nodes.find((n) => n.id === systemId);
  const systemSize = systemNode ? nodeSize(systemNode, 380, 220) : { w: 380, h: 220 };
  if (systemNode) {
    positioned.set(systemId, { x: -systemSize.w / 2, y: -systemSize.h / 2 });
  }

  const actors = nodes.filter((n) => n.type === "actor");
  const externals = nodes.filter((n) => n.type === "externalSystem");

  if (mode === "compass") {
    // Actors stacked in a LEFT column, externals stacked in a RIGHT column.
    // Each column's center sits one COMPASS_SIDE_GAP outside the system's
    // edge; cards inside the column are centered on that column-line.
    if (actors.length > 0) {
      const actorMaxW = actors.reduce((m, n) => Math.max(m, nodeSize(n).w), 0);
      const actorColumnX = -systemSize.w / 2 - COMPASS_SIDE_GAP - actorMaxW / 2;
      placeColumn(actors, actorColumnX, COMPASS_STACK_GAP, positioned);
    }
    if (externals.length > 0) {
      const extMaxW = externals.reduce((m, n) => Math.max(m, nodeSize(n).w), 0);
      const extColumnX = systemSize.w / 2 + COMPASS_SIDE_GAP + extMaxW / 2;
      placeColumn(externals, extColumnX, COMPASS_STACK_GAP, positioned);
    }
  } else {
    // "sides" — current arrangement: actors above, externals in rows below.
    if (actors.length > 0) {
      const actorH = rowHeight(actors);
      const actorCenterY = -systemSize.h / 2 - actorTopGap - actorH / 2;
      placeRow(actors, actorCenterY, 40, positioned);
    }

    if (externals.length > 0) {
      const rows: Node[][] = [];
      for (let i = 0; i < externals.length; i += perRow) {
        rows.push(externals.slice(i, i + perRow));
      }
      let cursorY = systemSize.h / 2 + extTopGap;
      for (const row of rows) {
        const rh = rowHeight(row);
        placeRow(row, cursorY + rh / 2, extColGap, positioned);
        cursorY += rh + extRowGap;
      }
    }
  }

  const laidOut = nodes.map((node) => {
    const pos = positioned.get(node.id);
    if (!pos) return node;
    return { ...node, position: pos };
  });

  return { nodes: laidOut, edges };
}
